import { Node, mergeAttributes, type Attribute } from '@tiptap/core';
import { Fragment, Slice } from '@tiptap/pm/model';
import type { Node as ProseMirrorNode, ResolvedPos, Mark } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { SvelteNodeViewRenderer } from 'svelte-tiptap';
import { dropPoint } from '@tiptap/pm/transform';

import BhsnReferenceComponent from './BhsnReferenceComponent.svelte';
import { dev } from '$app/environment';

// referenceNode가 갖는 attr. Node.create호출시 제네릭이나 기타방법으로 전달 불가하여 문서화를 위해 타입정의
export type BhsnReferenceNodeAttributes = {
    'data-id': Attribute;
    'data-name': Attribute;
    'data-type': Attribute;
    'focused-input': Attribute;
};

export interface BhsnReferenceOptions {
    HTMLAttributes: Record<string, unknown>;
}
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        reference: {
            toggleReference: () => ReturnType;
            updateReferenceContent: (attributes: Array<{ id: string; value?: string | ProseMirrorNode[] }>) => ReturnType;
            focusedInputReferenceContent: (id: string, scrollInto?: boolean, scrollIntoOption?: ScrollIntoViewOptions) => ReturnType;
            scrollIntoFocusedInputReferenceContent: (id: string, option?: ScrollIntoViewOptions) => ReturnType;
            blurInputReferenceContent: () => ReturnType;
            removeReferences: (names: string[]) => ReturnType;
            updateReferenceAttributes: ({ filterFn, transform }: { filterFn: (node: ProseMirrorNode) => boolean; transform: (node: ProseMirrorNode) => ProseMirrorNode['attrs'] }) => ReturnType;
        };
    }
}

export const BhsnReferenceExtension = Node.create<BhsnReferenceOptions>({
    name: 'reference',
    group: 'inline',
    content: 'text*',
    atom: true,
    inline: true,
    draggable: true,
    selectable: true,
    defining: true,

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    addAttributes() {
        return {
            'data-id': {
                default: '',
            },
            'data-name': {
                default: '',
            },
            'data-type': {
                default: undefined,
            },
            'focused-input': {
                default: undefined,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'bhsn-reference',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['bhsn-reference', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    addCommands() {
        return {
            toggleReference:
                () =>
                ({ tr, state, dispatch }) => {
                    const { selection, schema, doc } = state;
                    const { from, to } = selection;
                    const referenceNodeType = schema.nodes.reference;

                    const referenceNodePositions: number[] = [];
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (node.type === referenceNodeType) {
                            referenceNodePositions.push(pos);
                        }
                    });

                    if (referenceNodePositions.length) {
                        // 'reference' 노드를 텍스트로 변환
                        referenceNodePositions.forEach(pos => {
                            const node = tr.doc.nodeAt(pos);
                            const textContent = node!.attrs['data-id'];
                            tr.replaceWith(pos, pos + node!.nodeSize, schema.text(textContent, node!.marks));
                        });
                    } else {
                        // 선택된 텍스트를 가져와 'data-id' 속성에 설정한 뒤 'reference' 노드로 변환
                        const textContent = doc.textBetween(from, to);
                        const referenceNode = referenceNodeType.create({ 'data-id': textContent, 'data-name': textContent });
                        tr.replaceWith(from, to, referenceNode);
                    }

                    tr.setMeta('changedBhsnReference', true);
                    if (dispatch) dispatch(tr);
                    return true;
                },
            // 특정 id를 포함하는 값을 받았을때 data-id이 같은 값을 child content로 추가합니다.
            updateReferenceContent:
                attributes =>
                ({ tr, state, dispatch }) => {
                    const { doc, schema } = state;
                    const referenceNodeType = schema.nodes.reference;
                    const nodeInfos: Array<{ node: ProseMirrorNode; pos: number }> = [];

                    doc.descendants((node, pos) => {
                        if (node.type === referenceNodeType) {
                            if (attributes.some(attr => attr.id === node.attrs['data-id'] || attr.id === node.attrs['data-name'])) {
                                nodeInfos.push({ node, pos });
                            }
                        }
                    });

                    if (dev && nodeInfos.length === 0) {
                        console.warn(`updateReferenceContent: No reference nodes found for ${JSON.stringify(attributes)}`);
                        return true;
                    }

                    // 위치 문제를 피하기 위해 reverse 순서로 처리합니다.
                    for (let i = nodeInfos.length - 1; i >= 0; i--) {
                        const { node, pos } = nodeInfos[i];
                        const { value } = attributes.find(attr => attr.id === node.attrs['data-id'] || attr.id === node.attrs['data-name']) || {};

                        let fragment: Fragment;
                        if (value) {
                            let nodes: ProseMirrorNode[] = [];
                            if (typeof value === 'string') {
                                const parts = value.split('\n');
                                parts.forEach((part, index) => {
                                    if (part) {
                                        nodes.push(schema.text(part, node.marks));
                                    }
                                    if (index < parts.length - 1) {
                                        nodes.push(schema.nodes.hardBreak.create());
                                    }
                                });
                            } else if (Array.isArray(value)) {
                                nodes = value.flatMap((rawNode: unknown) => {
                                    // ProseMirror Node 타입 체크
                                    if (rawNode && typeof rawNode === 'object' && 'type' in rawNode) {
                                        const pmNode = rawNode as ProseMirrorNode;
                                        // paragraph 노드의 경우 내부 content를 처리
                                        if (pmNode.type.name === 'paragraph') {
                                            const content: ProseMirrorNode[] = [];
                                            pmNode.forEach(child => {
                                                if (child.type.name === 'text') {
                                                    content.push(schema.text(child.text || '', child.marks));
                                                } else {
                                                    content.push(schema.nodeFromJSON(child.toJSON()));
                                                }
                                            });
                                            return content;
                                        }
                                        return [pmNode];
                                    }
                                    // JSON 데이터를 Node로 변환
                                    try {
                                        return [schema.nodeFromJSON(rawNode)];
                                    } catch (e) {
                                        console.error('Failed to convert node:', e);
                                        return [];
                                    }
                                });
                                // 노드의 개수가 1 이상이면, 각 노드 사이에 ','를 텍스트로 갖는 text node를 삽입합니다.
                                if (nodes.length > 1) {
                                    const nodesWithCommas: ProseMirrorNode[] = [];
                                    nodes.forEach((node, index) => {
                                        nodesWithCommas.push(node);
                                        if (index < nodes.length - 1) {
                                            nodesWithCommas.push(schema.text(', ', []));
                                        }
                                    });
                                    nodes = nodesWithCommas;
                                }
                            }
                            fragment = Fragment.from(nodes);
                        } else {
                            fragment = Fragment.empty;
                        }

                        const updatedNode = node.copy(fragment);
                        tr.replaceWith(pos, pos + node.nodeSize, updatedNode);
                    }

                    tr.setMeta('changedBhsnReference', true);
                    if (dispatch) dispatch(tr);
                    return true;
                },
            // Input 영역에서 data-id에 해당하는 reference를 focus했을때 기존 focused-input를 모두 제거하고 해당 reference 에 focused-input attr을 추가합니다.
            focusedInputReferenceContent:
                (id, scrollInto = false, scrollIntoOption = { behavior: 'smooth', block: 'center' }) =>
                ({ tr, view, state, dispatch }) => {
                    const { doc, schema } = state;
                    const referenceNodeType = schema.nodes.reference;
                    let referenceNodePosition: number | null = null;

                    // 선택한 reference 노드에만 'focused-input' 속성을 추가합니다.
                    doc.descendants((node, pos) => {
                        if (node.type === referenceNodeType) {
                            const { attrs } = node;
                            if (id && id === node.attrs['data-id']) {
                                tr.setNodeMarkup(pos, null, { ...attrs, 'focused-input': true });
                                if (referenceNodePosition === null) referenceNodePosition = pos;
                            } else {
                                tr.setNodeMarkup(pos, null, { ...attrs, 'focused-input': undefined });
                            }
                        }
                    });

                    if (scrollInto && referenceNodePosition !== null) {
                        const referenceNodeView = view.nodeDOM(referenceNodePosition);
                        if (referenceNodeView) {
                            setTimeout(() => {
                                (referenceNodeView as Element).scrollIntoView(scrollIntoOption);
                            });
                        }
                    }
                    if (dispatch) dispatch(tr);
                    return true;
                },

            scrollIntoFocusedInputReferenceContent:
                (id, option = { behavior: 'smooth', block: 'center' }) =>
                ({ view, state: { doc, schema } }) =>
                    doc.descendants((node, pos) => {
                        if (node.type === schema.nodes.reference && id === node.attrs['data-id']) {
                            setTimeout(() => (view.nodeDOM(pos) as Element)?.scrollIntoView(option));
                            return false;
                        }
                    }) ?? true,
            // Input 영역에서 data-id에 해당하는 reference를 blur했을때 기존 focused-input를 모두 제거
            blurInputReferenceContent:
                () =>
                ({ tr, state, dispatch }) => {
                    const { doc, schema } = state;
                    const referenceNodeType = schema.nodes.reference;

                    // 'focused-input' 속성을 모두 제거합니다.
                    doc.descendants((node, pos) => {
                        if (node.type === referenceNodeType) {
                            const { attrs } = node;
                            const updatedAttrs = { ...attrs, 'focused-input': undefined };
                            tr.setNodeMarkup(pos, null, updatedAttrs);
                        }
                    });
                    if (dispatch) dispatch(tr);
                    return true;
                },
            removeReferences:
                (names: string[]) =>
                ({ state, tr }) => {
                    const positions: Array<[number, number]> = [];

                    state.doc.descendants((node, pos) => {
                        if (node.type === state.schema.nodes.reference && names.includes(node.attrs['data-name'])) {
                            positions.push([pos, node.nodeSize]);
                        }
                    });
                    positions.toReversed().forEach(([pos, size]) => tr.delete(pos, pos + size));

                    return true;
                },
            updateReferenceAttributes:
                ({ filterFn, transform }: { filterFn: (node: ProseMirrorNode) => boolean; transform: (node: ProseMirrorNode) => ProseMirrorNode['attrs'] }) =>
                ({ tr, state, dispatch, editor }) => {
                    if (!filterFn) return true;

                    const updates: Array<{ pos: number; node: ProseMirrorNode; size: number }> = [];

                    state.doc.descendants((node, pos) => {
                        if (filterFn(node) && node.type === state.schema.nodes.reference) {
                            updates.push({ pos, node, size: node.nodeSize });
                        }
                    });

                    if (updates.length === 0) return true;

                    updates.reverse().forEach(({ pos, node }) => {
                        const newAttrs = transform(node);
                        // 내부 텍스트 노드의 marks를 가져옴
                        const marks = node.content.firstChild?.marks || [];
                        // 새로운 텍스트 노드를 생성하고 기존 marks는 유지
                        const textNode = editor.state.schema.text(newAttrs['data-name'], marks);
                        const newNode = state.schema.nodes.reference.create(newAttrs, textNode);
                        tr.replaceWith(pos, pos + node.nodeSize, newNode);
                    });

                    if (dispatch) {
                        dispatch(tr);
                        return true;
                    }
                    return false;
                },
        };
    },
    addNodeView() {
        return SvelteNodeViewRenderer(BhsnReferenceComponent);
    },
    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handleDrop: (view, event, slice, moved) => {
                        if (moved) return false;

                        const data = event.dataTransfer?.getData('application/json');
                        if (!data) return false;

                        const payload = JSON.parse(data);
                        if (!payload) return false;

                        // type이 없는 경우 (reference node가 아닌 경우) 드롭은 무시
                        if (!('type' in payload)) return false;
                        // inline reference 드롭이 아닌 경우 무시
                        if (payload?.referenceType !== 'inline') return false;

                        // 드롭 위치 계산
                        const coordinates = view.posAtCoords({
                            left: event.clientX,
                            top: event.clientY,
                        });
                        if (!coordinates) return false;

                        const { tr, schema, doc } = view.state;
                        const $pos = doc.resolve(coordinates.pos);

                        // reference 내부인지 확인
                        const isInsideReference = findParentByNodeTypeName($pos, 'reference');
                        // blockReference 위에 있는지 확인
                        const isOnBlockReference = findParentByNodeTypeName($pos, 'blockReference');
                        // slotReference 내부인지 확인
                        const isInsideSlotReference = findParentByNodeTypeName($pos, 'slotReference');

                        // reference 내부로의 드롭 방지
                        if (isInsideReference) {
                            return true;
                        }

                        // blockReference 위에 있고 slotReference 내부가 아니면 드롭 무시
                        if (isOnBlockReference && !isInsideSlotReference) {
                            return false;
                        }

                        try {
                            // 드롭 위치의 marks 가져오기 (lineSpacing 제외)
                            let marks: Mark[] = [];
                            if ($pos.parent.type.name === 'paragraph') {
                                // 현재 위치의 marks를 가져옴
                                const textNode = $pos.parent.childAfter($pos.parentOffset)?.node;
                                if (textNode && textNode.marks) {
                                    marks = Array.from(textNode.marks).map(mark => {
                                        if (mark.type.name === 'textStyle') {
                                            const attrs = { ...mark.attrs };
                                            delete attrs.lineSpacing;
                                            return mark.type.create(attrs);
                                        }
                                        return mark;
                                    });
                                } else {
                                    // 이전 위치의 marks를 가져옴
                                    const prevTextNode = $pos.parent.childBefore($pos.parentOffset)?.node;
                                    if (prevTextNode && prevTextNode.marks) {
                                        marks = Array.from(prevTextNode.marks).map(mark => {
                                            if (mark.type.name === 'textStyle') {
                                                const attrs = { ...mark.attrs };
                                                delete attrs.lineSpacing;
                                                return mark.type.create(attrs);
                                            }
                                            return mark;
                                        });
                                    }
                                }
                            }

                            // 해당 marks를 적용한 text 노드 생성
                            const textNode = schema.text(payload.name, marks);

                            const referenceNode = schema.nodes.reference.create(
                                {
                                    'data-id': payload.id,
                                    'data-name': payload.name,
                                    'data-type': payload.type,
                                },
                                textNode,
                                marks,
                            );

                            // Slice 생성 후 dropPoint 계산
                            const slice = new Slice(Fragment.from(referenceNode), 0, 0);
                            const dropPos = dropPoint(doc, coordinates.pos, slice);
                            if (dropPos == null) return false;

                            // 트랜잭션 실행 및 상태 업데이트
                            const transaction = tr.insert(dropPos, referenceNode);

                            view.dispatch(transaction);
                            return true;
                        } catch (error) {
                            console.error('Error in handleDrop:', error);
                            return false;
                        }
                    },
                },
            }),
        ];
    },
});

/**
 * pos 위치로부터 상위 노드를 탐색하여 typeName과 일치하는 노드가 있는지 확인합니다.
 */
const findParentByNodeTypeName = (pos: ResolvedPos, typeName: string) => {
    let depth = pos.depth;
    // 문서의 최상위까지 모든 부모 노드를 확인
    while (depth >= 0) {
        const node = pos.node(depth);
        if (node.type.name === typeName) return true;
        depth--;
    }
    return false;
};
